import { List, ListItemProperty } from "@/features/list";
import { trpc } from "@/hooks/trpc";
import { CleaningProcedure } from "@brewbase/db/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute("/settings/cleaning-procedures/")({
  component: () => (
    <RequireCapability cap="cleaning-procedures:edit">
      <RouteComponent />
    </RequireCapability>
  ),
});

function RouteComponent() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: recordTypes, isLoading } =
    trpc.cleaning.procedures.list.useQuery();

  const createMutation = trpc.cleaning.procedures.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Created "${data.name}"`);
      utils.cleaning.procedures.list.invalidate();
      navigate({
        to: "/settings/cleaning-procedures/$procedure_id",
        params: { procedure_id: data.id },
      });
    },
    onError: (err) => {
      toast.error(`Failed to create: ${err.message}`);
    },
  });

  const handleCreate = () => {
    createMutation.mutate({
      name: "New Cleaning Type",
      code: `CLEAN_${Date.now()}`,
    });
  };

  if (isLoading || !recordTypes) {
    return (
      <div className="container mx-auto px-4 py-6">
        Loading cleaning procedures...
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 flex flex-col grow min-h-0 gap-4">
      <div>
        <h1 className="text-lg font-semibold">Cleaning Procedures</h1>
        <p className="text-sm text-muted-foreground">
          Manage cleaning procedure definitions and their forms.
        </p>
      </div>
      <List
        data={recordTypes.map((t) => ({
          id: t.id.toString(),
          href: `/settings/cleaning-procedures/${t.id}`,
          properties: t,
        }))}
        properties={listColumns}
        customToolbar={
          <Button
            variant="outline"
            onClick={handleCreate}
            disabled={createMutation.isPending}
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Cleaning Type
          </Button>
        }
      />
    </div>
  );
}

const listColumns: ListItemProperty<
  CleaningProcedure,
  keyof CleaningProcedure
>[] = [
  {
    key: "name",
    label: "Name",
    fieldType: "string",
  },
  {
    key: "code",
    label: "Code",
    fieldType: "string",
  },
  {
    key: "description",
    label: "Description",
    fieldType: "string",
  },
  {
    key: "version",
    label: "Version",
    fieldType: "string",
  },
  {
    key: "is_active",
    label: "Active",
    fieldType: "boolean",
  },
];
